/**
 * Header.jsx
 *
 * Top navigation bar rendered on every page.
 *
 * Responsibilities:
 *   - Displays the app logo (links to /)
 *   - Desktop navigation links (Upload, Results, History, AI Insights)
 *   - Credit balance indicator and account dropdown for authenticated users
 *   - Sign In / Create Account buttons for unauthenticated visitors
 *   - Mobile hamburger menu with the same links + user info
 *
 * Auth data is sourced from AuthContext; prop-based overrides (user, onLogout,
 * onPurchaseCredits, onNavigate) are supported for legacy call sites but
 * context values take precedence.
 */

import React, { useState, useEffect, useRef } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import Icon from '../AppIcon';
import { useAuth } from '../../contexts/AuthContext';
import CreditStatusIndicator from './CreditStatusIndicator';
import AccountDropdown from './AccountDropdown';

// ─── Navigation link definitions ─────────────────────────────────────────────

/** Desktop navigation links */
const NAV_LINKS = [
  { label: 'Upload Photos', path: '/photo-upload-interface', icon: 'Upload', description: 'Upload and analyze your photos' },
  { label: 'View Results',  path: '/ranking-results-display', icon: 'BarChart3', description: 'See your photo rankings' },
  { label: 'History',       path: '/dashboard-analysis-history', icon: 'History', description: 'View past analyses' },
  { label: 'Blog',          path: '/blog', icon: 'BookOpen', description: 'Photo tips and dating advice' },
];

/** Mobile menu links (superset of desktop links) */
const MOBILE_LINKS = [
  { label: 'Upload Photos', path: '/photo-upload-interface', icon: 'Upload', description: 'Upload and analyze your photos' },
  { label: 'View Results',  path: '/ranking-results-display', icon: 'BarChart3', description: 'See your photo rankings' },
  { label: 'Dashboard',     path: '/dashboard-analysis-history', icon: 'LayoutDashboard', description: 'View analysis history' },
  { label: 'Blog',          path: '/blog', icon: 'BookOpen', description: 'Photo tips and dating advice' },
  { label: 'Settings',      path: '/settings', icon: 'Settings', description: 'Manage your account' },
  { label: 'FAQ',           path: '/frequently-asked-questions', icon: 'HelpCircle', description: 'Get help and answers' },
];

// ─── Component ────────────────────────────────────────────────────────────────

/**
 * @param {object}   [props]
 * @param {object}   [props.user]               - Legacy user prop (context takes precedence)
 * @param {Function} [props.onNavigate]          - Optional navigation callback
 * @param {Function} [props.onPurchaseCredits]   - Optional purchase credits callback
 * @param {Function} [props.onLogout]            - Optional logout callback
 */
const Header = ({ user: userProp, onNavigate, onPurchaseCredits, onLogout }) => {
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();
  const mobileMenuRef = useRef(null);

  const { user: authUser, userProfile, creditBalance, signOut, isAuthenticated } = useAuth();

  // Prefer context data; fall back to legacy props
  const displayName = userProfile?.full_name || authUser?.user_metadata?.full_name || userProp?.name || 'User';
  const displayEmail = authUser?.email || userProp?.email || '';
  const creditsRemaining = creditBalance ?? userProp?.creditsRemaining ?? 0;

  // Normalised user object passed to child components
  const user = { name: displayName, email: displayEmail, avatar: null, creditsRemaining };

  // ─── Handlers ──────────────────────────────────────────────────────────────

  const handleLogout = async () => {
    if (onLogout) { onLogout(); return; }
    await signOut();
    navigate('/', { replace: true });
  };

  const handlePurchaseCredits = () => {
    if (onPurchaseCredits) { onPurchaseCredits(); return; }
    navigate('/purchase-credits');
  };

  const handleNavigation = (path) => {
    setIsMobileMenuOpen(false);
    onNavigate?.(path);
  };

  const isActive = (path) => location?.pathname === path;

  // ─── Mobile menu: close on outside click or Escape key ─────────────────────

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (mobileMenuRef?.current && !mobileMenuRef?.current?.contains(e?.target)) {
        setIsMobileMenuOpen(false);
      }
    };
    const handleEscape = (e) => {
      if (e?.key === 'Escape') setIsMobileMenuOpen(false);
    };

    if (isMobileMenuOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      document.addEventListener('keydown', handleEscape);
      document.body.style.overflow = 'hidden'; // prevent background scroll
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('keydown', handleEscape);
      document.body.style.overflow = 'unset';
    };
  }, [isMobileMenuOpen]);

  // ─── Render ────────────────────────────────────────────────────────────────

  return (
    <>
      {/* ── Desktop header ─────────────────────────────────────────────── */}
      <header className="header-container">
        <div className="header-content">
          {/* Logo */}
          <Link to="/" className="header-logo">
            <div className="header-logo-icon">
              <Icon name="Camera" size={24} color="var(--color-primary)" />
            </div>
          </Link>

          {/* Desktop nav links */}
          <nav className="header-nav">
            {NAV_LINKS?.map((item) => (
              <Link
                key={item?.path}
                to={item?.path}
                className={`header-nav-link ${isActive(item?.path) ? 'active' : ''}`}
                onClick={() => handleNavigation(item?.path)}
              >
                {item?.label}
              </Link>
            ))}

            {/* AI Insights — premium link with badge */}
            <Link
              to="/gemini-profile-insights"
              className={`header-nav-link premium-link ${isActive('/gemini-profile-insights') ? 'active' : ''}`}
              onClick={() => handleNavigation('/gemini-profile-insights')}
            >
              <Icon name="Sparkles" size={14} color="#9333EA" />
              <span>AI Insights</span>
              <span className="premium-badge">PRO</span>
            </Link>
          </nav>

          {/* Right-side actions */}
          <div className="header-actions">
            {isAuthenticated ? (
              <>
                <CreditStatusIndicator
                  creditsRemaining={creditsRemaining}
                  onPurchaseCredits={handlePurchaseCredits}
                />
                <AccountDropdown
                  user={user}
                  onLogout={handleLogout}
                  onPurchaseCredits={handlePurchaseCredits}
                />
              </>
            ) : (
              <div className="flex items-center gap-2">
                <Link
                  to="/auth"
                  state={{ tab: 'signin' }}
                  className="px-4 py-2 text-sm font-semibold text-gray-700 hover:text-purple-600 transition-colors"
                >
                  Sign In
                </Link>
                <Link
                  to="/auth"
                  state={{ tab: 'signup' }}
                  className="px-4 py-2 text-sm font-semibold text-white bg-gradient-to-r from-purple-600 to-blue-500 rounded-xl hover:from-purple-700 hover:to-blue-600 transition-all shadow-sm"
                >
                  Create Account
                </Link>
              </div>
            )}
          </div>
        </div>
      </header>
      {/* ── Mobile hamburger button ─────────────────────────────────────── */}
      <button
        className="mobile-menu-button"
        onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
        aria-label={isMobileMenuOpen ? 'Close menu' : 'Open menu'}
        aria-expanded={isMobileMenuOpen}
      >
        <Icon name={isMobileMenuOpen ? 'X' : 'Menu'} size={24} color="var(--color-foreground)" />
      </button>
      {/* ── Mobile menu overlay ─────────────────────────────────────────── */}
      {isMobileMenuOpen && (
        <div className="mobile-menu-overlay" ref={mobileMenuRef}>
          <div className="mobile-menu-content">
            {/* Auth state: credit indicator or sign-in/up buttons */}
            {isAuthenticated ? (
              <div className="mb-6">
                <CreditStatusIndicator
                  creditsRemaining={creditsRemaining}
                  onPurchaseCredits={handlePurchaseCredits}
                  isMobile
                />
              </div>
            ) : (
              <div className="mb-6 flex flex-col gap-2">
                <Link
                  to="/auth"
                  state={{ tab: 'signup' }}
                  className="w-full py-3 text-center text-sm font-semibold text-white bg-gradient-to-r from-purple-600 to-blue-500 rounded-xl"
                  onClick={() => setIsMobileMenuOpen(false)}
                >
                  Create Account
                </Link>
                <Link
                  to="/auth"
                  state={{ tab: 'signin' }}
                  className="w-full py-3 text-center text-sm font-semibold text-gray-700 border border-gray-200 rounded-xl"
                  onClick={() => setIsMobileMenuOpen(false)}
                >
                  Sign In
                </Link>
              </div>
            )}

            {/* Navigation links */}
            {MOBILE_LINKS?.map((item) => (
              <Link
                key={item?.path}
                to={item?.path}
                className={`mobile-menu-link ${isActive(item?.path) ? 'active' : ''}`}
                onClick={() => handleNavigation(item?.path)}
              >
                <div className="flex items-center gap-3">
                  <Icon name={item?.icon} size={20} />
                  <div className="flex-1">
                    <div className="font-medium">{item?.label}</div>
                    <div className="text-xs text-muted-foreground caption mt-0.5">{item?.description}</div>
                  </div>
                </div>
              </Link>
            ))}

            {/* Authenticated user info + sign-out */}
            {isAuthenticated && (
              <div className="mt-6 pt-6 border-t border-border">
                <div className="px-6 py-3 mb-2">
                  <div className="font-semibold text-foreground">{user?.name}</div>
                  <div className="text-sm text-muted-foreground caption">{user?.email}</div>
                </div>
                <button
                  className="mobile-menu-link w-full text-left"
                  onClick={() => { setIsMobileMenuOpen(false); handleLogout(); }}
                >
                  <div className="flex items-center gap-3">
                    <Icon name="LogOut" size={20} />
                    <span>Sign Out</span>
                  </div>
                </button>
              </div>
            )}
          </div>
        </div>
      )}
    </>
  );
};

export default Header;